import { Box, Button, Grid2, Paper, Stack, Typography } from "@mui/material";
import { Formik } from "formik";
import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import FormikTextField from "../../components/form/FormikTextField";
import { useNavigate, useParams } from "react-router";
import { vendorsUrl } from "../../utils/urls";
import { toast } from "react-hot-toast";
import { hideLoader, showLoader } from "../../store/loaderSlice";
import { useDispatch } from "react-redux";
import axiosInstance from "../../utils/axiosInstance";
import PageHeader from "../../components/page-layout/PageHeader";
import { Add } from "@mui/icons-material";

const EditVendor = () => {
  const [vendorData, setVendorData] = useState(
    {
      name: "",
      email: "",
      phoneNumber: "",
    }
  );
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { cognitoId } = useParams();

  // Fetch vendor details on mount
  useEffect(() => {
    dispatch(showLoader("Fetching Vendor Data..."));
    axiosInstance
      .get(`${vendorsUrl}/${cognitoId}`)
      .then((response) => {
        const data = response?.data?.data || response?.data || {};
        const phoneUI = data.phoneNumber?.slice(-10) || "";
        setVendorData({ ...data, phoneNumber: phoneUI });
      })
      .catch((error) => {
        console.error("Error fetching vendor data:", error);
        toast.error("Failed to load vendor details.");
      })
      .finally(() => {
        dispatch(hideLoader());
      });
  }, [cognitoId, dispatch]);

  const validationSchema = Yup.object({
     name: Yup.string().required("Name is required"),
     email: Yup.string().email("Invalid email").required("Email is required"),
     phoneNumber: Yup.string()
       .matches(/^\d{10}$/, "Please enter a valid 10 digit mobile number")
       .required("Mobile number is required"),
   });

  const onSubmit = (values, { setSubmitting }) => {
    dispatch(showLoader("Updating Vendor Data..."));

    const submitData = {
      ...values,
      phoneNumber: `+91${values.phoneNumber}`
    };

    axiosInstance
      .patch(`${vendorsUrl}/${cognitoId}`, submitData)
      .then(() => {
        toast.success("Vendor updated successfully!");
        navigate(-1);
      })
      .catch((err) => {
        console.error(err);
        toast.error(err.response?.data?.error || "Failed to update vendor.");
      })
      .finally(() => {
        dispatch(hideLoader());
        setSubmitting(false);
      });
  };

  return (
    <Stack spacing={5} height="100%">
      <Formik
        enableReinitialize
        initialValues={vendorData}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ handleSubmit, values }) => (
          <>
            <PageHeader
              title={"Edit Vendor"}
              buttonTitle={"Save Changes"}
              buttonOnClick={handleSubmit}
              showBack={true}
            />

            <Stack direction={{ xs: "column", md: "row" }} spacing={3} px={2} py={3} flex={1}>
              {/* Card UI */}
              <Paper elevation={3} sx={{ p: 3, minWidth: 320, maxWidth: 400, display: "flex", height: "min-content" }}>
                {/* Avatar */}
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    bgcolor: "primary.main",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 32,
                    mr: 3,
                  }}
                >
                  {values.name ? values.name.charAt(0).toUpperCase() : <Add fontSize="large" />}
                </Box>
                {/* Vendor Details */}
                <Stack spacing={1}>
                  <Typography variant="h6">{values.name || "Name"}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {values.email || "Email"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {values.phoneNumber ? `+91${values.phoneNumber}` : "Mobile Number"}
                  </Typography>
                </Stack>
              </Paper>

              {/* Form Fields */}
              <Paper elevation={1} sx={{ flex: 1, p: 3 }}>
                <Grid2 container spacing={2}>
                  <Grid2  size={{ xs: 12, md: 6 }}>
                    <FormikTextField name="name" label="Name" />
                  </Grid2>
                  <Grid2  size={{ xs: 12, md: 6 }}>
                    <FormikTextField
                      name="phoneNumber"
                      label="Mobile Number"
                      startAdornment="+91"
                    />
                  </Grid2>
                  <Grid2  size={{ xs: 12, md: 6 }}>
                    <FormikTextField name="email" label="Email" />
                  </Grid2>
                </Grid2>
              </Paper>
            </Stack>
          </>
        )}
      </Formik>
    </Stack>
  );
};

export default EditVendor;
